/**
 * Utilidades para manejo de nombres en Club Mia
 * Formato esperado: "Apellido Paterno Apellido Materno Primer Nombre Segundo Nombre"
 * Ejemplo: "LESCANO BEJARANO AMPARO" -> Display: "Amparo Lescano"
 */

/**
 * Limpia un texto eliminando comas y espacios extra
 * @param {string} text - Texto a limpiar
 * @returns {string} Texto limpio
 */
export const cleanText = (text) => {
  if (!text) return '';
  return text.replace(/,/g, '').trim().replace(/\s+/g, ' ');
};

/**
 * Convierte un texto a formato título (Primera letra mayúscula, demás minúsculas)
 * @param {string} text - Texto a convertir
 * @returns {string} Texto en formato título
 */
export const toTitleCase = (text) => {
  if (!text) return '';
  const cleanedText = cleanText(text);
  return cleanedText.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
};

/**
 * Extrae el primer nombre y apellido paterno para mostrar en el dashboard
 * @param {string} personName - Nombre completo separado por espacios
 * @returns {string} "Primer Nombre Apellido Paterno" en formato título
 */
export const extractDisplayName = (personName) => {
  if (!personName) return '';

  // Clean the name first to remove commas
  const cleanedName = cleanText(personName);
  const parts = cleanedName.split(' ').filter(part => part.length > 0);

  if (parts.length >= 3) {
    // parts[0] = Apellido Paterno
    // parts[1] = Apellido Materno
    // parts[2] = Primer Nombre
    // parts[3] = Segundo Nombre (opcional)
    const apellidoPaterno = toTitleCase(parts[0]);
    const primerNombre = toTitleCase(parts[2]);

    return `${primerNombre} ${apellidoPaterno}`;
  } else if (parts.length === 2) {
    // Si solo hay 2 partes, asumir "Apellido Nombre"
    return `${toTitleCase(parts[1])} ${toTitleCase(parts[0])}`;
  } else {
    // Fallback: si no tiene el formato esperado, usar como está
    return toTitleCase(personName);
  }
};

/**
 * Extrae solo el primer nombre
 * @param {string} personName - Nombre completo separado por espacios
 * @returns {string} Solo el primer nombre en formato título
 */
export const extractFirstName = (personName) => {
  if (!personName) return '';

  const parts = personName.trim().split(' ').filter(part => part.length > 0);

  if (parts.length >= 3) {
    // parts[2] = Primer Nombre
    return toTitleCase(parts[2]);
  } else if (parts.length === 2) {
    // Si solo hay 2 partes, asumir que la segunda es el nombre
    return toTitleCase(parts[1]);
  } else {
    // Fallback: usar el nombre completo
    return toTitleCase(personName);
  }
};

/**
 * Extrae el apellido paterno
 * @param {string} personName - Nombre completo separado por espacios
 * @returns {string} Apellido paterno en formato título
 */
export const extractLastName = (personName) => {
  if (!personName) return '';

  const parts = personName.trim().split(' ').filter(part => part.length > 0);

  if (parts.length >= 1) {
    // parts[0] = Apellido Paterno
    return toTitleCase(parts[0]);
  } else {
    return '';
  }
};

/**
 * Extrae el apellido materno
 * @param {string} personName - Nombre completo separado por espacios
 * @returns {string} Apellido materno en formato título
 */
export const extractMaternalName = (personName) => {
  if (!personName) return '';

  const parts = personName.trim().split(' ').filter(part => part.length > 0);

  if (parts.length >= 2) {
    // parts[1] = Apellido Materno
    return toTitleCase(parts[1]);
  } else {
    return '';
  }
};

/**
 * Extrae el segundo nombre (si existe)
 * @param {string} personName - Nombre completo separado por espacios
 * @returns {string} Segundo nombre en formato título o cadena vacía
 */
export const extractSecondName = (personName) => {
  if (!personName) return '';

  const parts = personName.trim().split(' ').filter(part => part.length > 0);

  if (parts.length >= 4) {
    // parts[3] = Segundo Nombre
    return toTitleCase(parts[3]);
  } else {
    return '';
  }
};

/**
 * Separa un nombre completo en sus componentes
 * @param {string} personName - Nombre completo separado por espacios
 * @returns {Object} Objeto con todos los componentes del nombre en formato título
 */
export const parseFullName = (personName) => {
  if (!personName) {
    return {
      apellidoPaterno: '',
      apellidoMaterno: '',
      primerNombre: '',
      segundoNombre: '',
      displayName: '',
      fullName: ''
    };
  }

  const parts = personName.trim().split(' ').filter(part => part.length > 0);

  const apellidoPaterno = toTitleCase(parts[0] || '');
  const apellidoMaterno = toTitleCase(parts[1] || '');
  const primerNombre = toTitleCase(parts[2] || '');
  const segundoNombre = toTitleCase(parts[3] || '');

  const displayName = primerNombre && apellidoPaterno
    ? `${primerNombre} ${apellidoPaterno}`
    : toTitleCase(personName);

  return {
    apellidoPaterno,
    apellidoMaterno,
    primerNombre,
    segundoNombre,
    displayName,
    fullName: personName // Mantener el original para referencia
  };
};
